import { supabase } from "../supabaseClient";

export type PiPaymentDTO = {
  identifier?: string;
  user_uid?: string;
  amount?: number;
  memo?: string;
  metadata?: Record<string, unknown>;
  from_address?: string;
  to_address?: string;
  direction?: "user_to_app" | "app_to_user";
  created_at?: string;
  network?: "Pi Network" | "Pi Testnet";
  transaction?: {
    txid?: string;
    verified?: boolean;
    _link?: string;
  } | null;
  status?: {
    developer_approved?: boolean;
    transaction_verified?: boolean;
    developer_completed?: boolean;
    cancelled?: boolean;
    user_cancelled?: boolean;
  };
};

export type PiAuthResult = {
  user: {
    uid: string;
    username?: string;
    wallet_address?: string;
  };
  accessToken: string;
};

export type PiPaymentData = {
  amount: number;
  memo: string;
  metadata?: Record<string, unknown>;
};

export type PiPaymentCallbacks = {
  onReadyForServerApproval: (paymentId: string) => void;
  onReadyForServerCompletion: (paymentId: string, txid: string) => void;
  onCancel: (paymentId: string) => void;
  onError: (error: Error, payment?: PiPaymentDTO) => void;
};

type PiSDK = {
  authenticate: (
    scopes: Array<"username" | "payments" | "wallet_address">,
    onIncompletePaymentFound?: (payment: PiPaymentDTO) => void,
  ) => Promise<PiAuthResult>;
  createPayment: (
    paymentData: PiPaymentData,
    callbacks: PiPaymentCallbacks,
  ) => void;
  nativeFeaturesList: () => Promise<unknown>;
  openShareDialog: (title: string, message: string) => void;
  openUrlInSystemBrowser: (url: string) => Promise<void>;
};

declare global {
  interface Window {
    Pi?: PiSDK;
  }
}

// 🛡️ Helper function to safely ensure commands only run when the global environment is live
const getLivePiSdk = (): PiSDK => {
  if (typeof window === "undefined" || !window.Pi) {
    throw new Error("Pi SDK is unavailable. Ensure this action is executed inside the Pi Browser environment.");
  }
  return window.Pi;
};

/**
 * 🚀 Added explicit function export to satisfy Profile.tsx imports and resolve compilation mismatch
 */
export const initPiSdk = async (): Promise<boolean> => {
  if (typeof window !== "undefined" && window.Pi) {
    return true;
  }
  console.warn("Pi SDK not detected on global window tree context layer yet.");
  return false;
};

/**
 * Initiates the authentication prompt against the active window layer.
 */
export const authenticatePiUser = async (
  onIncompletePaymentFound?: (payment: PiPaymentDTO) => void,
) => {
  const Pi = getLivePiSdk();
  return Pi.authenticate(
    ["username", "payments", "wallet_address"],
    onIncompletePaymentFound,
  );
};

/**
 * Authenticates the user session with explicit wallet binding permissions.
 */
export const authenticatePiUserWithWallet = async (
  onIncompletePaymentFound?: (payment: PiPaymentDTO) => void,
) => {
  const Pi = getLivePiSdk();
  return Pi.authenticate(
    ["username", "payments", "wallet_address"],
    onIncompletePaymentFound,
  );
};

/**
 * Fires the token verification payload over to your Supabase Auth proxy handlers.
 */
export const linkPiUserAccount = async (accessToken: string) => {
  const { data, error } = await supabase.functions.invoke("pi-auth-verify", {
    body: { accessToken },
  });

  if (error) {
    throw new Error(
      error?.message || "Failed to communicate with authentication server",
    );
  }

  return data;
};

/**
 * Spawns a native user checkout interface container directly inside the browser framework.
 */
export const createPiPayment = async (
  paymentData: PiPaymentData,
  callbacks: PiPaymentCallbacks,
) => {
  const Pi = getLivePiSdk();
  Pi.createPayment(paymentData, callbacks);
};